import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { inject, observer } from "mobx-react";
import BroadcastStore from "../_stores/broadcast/BroadcastStore";
import BroadcastStreamStore from "../_stores/broadcast-stream/BroadcastStreamStore";
import ProfileStore from "common/my-page/stores/profile/ProfileStore";
import { IC2C } from "../_stores/broadcast/interfaces";
import { WebRTCStatus } from "../_stores/broadcast/enums";
import config from "core/config";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindBox from "library/components/_tailwind/box";
import TailwindIcon from "library/components/_tailwind/icon";
import TailwindText from "library/components/_tailwind/text";
import WebrtcViewer from "../webrtc-player/c2c-webRTC-viewer";
import { LiveKitCam2Cam } from "containers/livekit/Cam2Cam";
import "./styles.scss";

interface MobileBroadcastC2CProps {
  broadcastStore?: BroadcastStore;
  broadcastStreamStore?: BroadcastStreamStore;
  profileStore?: ProfileStore;
}

type WebRTCSetup = {
  origin?: string;
  applicationName?: string;
  streamName?: string;
  channelName?: string;
  token?: string;
};

const STREAMSRC = "streamray.com";

// Default floating player size
const PLAYER_WIDTH = 140;
const PLAYER_HEIGHT = 180;

const MobileBroadcastC2C: React.FC<MobileBroadcastC2CProps> = ({
  broadcastStore,
  broadcastStreamStore,
  profileStore,
}) => {
  const { c2cHash, setWebRTCStatus } = broadcastStore!;
  const { getCam2camStreamCredentials } = broadcastStreamStore!;
  const { profile } = profileStore!;

  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [muted, setMuted] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const viewerRef = useRef<any>(null);

  // Draggable position state (for floating mode)
  const [position, setPosition] = useState({ x: 10, y: 70 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  const c2cList = useMemo(() => {
    return Object.values(c2cHash || {});
  }, [c2cHash]);

  const currentC2C: IC2C | null = useMemo(() => {
    if (c2cList.length === 0) return null;
    const safeIndex = Math.min(currentIndex, c2cList.length - 1);
    return c2cList[safeIndex] || null;
  }, [c2cList, currentIndex]);

  // Reset index when list changes
  useEffect(() => {
    if (currentIndex >= c2cList.length && c2cList.length > 0) {
      setCurrentIndex(c2cList.length - 1);
    }
  }, [c2cList.length, currentIndex]);

  // Reset WebRTC status when no c2c viewers
  useEffect(() => {
    if (c2cList.length === 0) {
      setWebRTCStatus(WebRTCStatus.Stopped);
    }
  }, [c2cList.length, setWebRTCStatus]);

  const getWebRTCSetup = useCallback((setup: WebRTCSetup) => {
    if (
      setup.origin &&
      setup.applicationName &&
      setup.channelName &&
      setup.token &&
      setup.streamName
    ) {
      const { origin, applicationName, channelName, streamName, token } = setup;
      const dashOrigin = origin.replace(/\./g, "-");
      return {
        applicationName: `${applicationName}/${channelName}`,
        streamName: `${streamName}?token=${token}`,
        wssUrl: `wss://h${dashOrigin}.${STREAMSRC}/webrtc-session.json`,
      };
    }
    return null;
  }, []);

  // Initialize/reinitialize WebRTC player when current c2c changes
  useEffect(() => {
    if (!currentC2C || !videoRef.current || currentC2C.isLivekit) return;
    if (config.disableWebrtcC2CVideo) return;

    let viewer: any;

    const initPlayer = async () => {
      try {
        const credentials = await getCam2camStreamCredentials(
          currentC2C.memberName
        );
        if (
          !credentials?.origin ||
          !credentials?.applicationName ||
          !credentials?.token
        ) {
          console.log("Mobile C2C: Could not fetch credentials");
          return;
        }

        const setup = getWebRTCSetup(credentials);
        if (!setup) return;

        const _config = {
          videoEl: videoRef.current,
          setup,
          onClose: () => {},
          onError: (e: any) => {
            console.log("Mobile C2C error:", e);
          },
          onVideoError: () => {
            console.log("Mobile C2C video error");
          },
          alwaysPlayTrack: false,
        };

        setTimeout(() => {
          try {
            viewer = new WebrtcViewer(_config);
            viewerRef.current = viewer;
          } catch (e) {
            console.log("Mobile C2C Player init error", e);
          }
        }, 500);
      } catch (e) {
        console.log("Mobile C2C init failed", e);
      }
    };

    initPlayer();

    return () => {
      if (viewer) {
        viewer.stop();
        viewerRef.current = null;
      }
    };
  }, [currentC2C, getCam2camStreamCredentials, getWebRTCSetup]);

  // Drag handlers (only active when not in full screen)
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (isFullScreen) return;
      // Check if the touch target is a button or interactive element
      const target = e.target as HTMLElement;
      if (
        target.closest("button") ||
        target.tagName === "BUTTON" ||
        // Also skip for LiveKit controls (mute button, etc.) which use spans with onClick
        target.closest(".BroadcastC2CRTCViewer__info") ||
        target.closest(".BroadcastC2CRTCViewer__icon")
      ) {
        return; // Don't start drag on interactive element touches
      }
      e.preventDefault();
      const touch = e.touches[0];
      dragStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        posX: position.x,
        posY: position.y,
      };
      setIsDragging(true);
    },
    [position, isFullScreen]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging || isFullScreen) return;
      e.preventDefault();

      const touch = e.touches[0];
      const deltaX = touch.clientX - dragStartRef.current.x;
      const deltaY = touch.clientY - dragStartRef.current.y;

      const newX = dragStartRef.current.posX + deltaX; // + for left positioning
      const newY = dragStartRef.current.posY + deltaY;

      const maxX = window.innerWidth - PLAYER_WIDTH - 10;
      const maxY = window.innerHeight - PLAYER_HEIGHT - 120; // Extra buffer for bottom sheet

      setPosition({
        x: Math.max(10, Math.min(maxX, newX)),
        y: Math.max(60, Math.min(maxY, newY)),
      });
    },
    [isDragging, isFullScreen]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (isDragging) {
        e.preventDefault();
      }
      setIsDragging(false);
    },
    [isDragging]
  );

  const handlePrevViewer = useCallback(() => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : c2cList.length - 1));
  }, [c2cList.length]);

  const handleNextViewer = useCallback(() => {
    setCurrentIndex(prev => (prev < c2cList.length - 1 ? prev + 1 : 0));
  }, [c2cList.length]);

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !muted;
    }
    setMuted(!muted);
  }, [muted]);

  const toggleFullScreen = useCallback(() => {
    setIsFullScreen(prev => !prev);
  }, []);

  // Don't render if no c2c viewers
  if (c2cList.length === 0 || !currentC2C) {
    return null;
  }

  // Full screen styles
  const fullScreenStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100dvh", // Use dynamic viewport height to account for mobile browser UI
    zIndex: 100,
    backgroundColor: "#000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  // Floating (draggable) styles
  const floatingStyle: React.CSSProperties = {
    position: "absolute",
    top: position.y,
    left: position.x,
    width: PLAYER_WIDTH,
    height: PLAYER_HEIGHT,
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: isDragging
      ? "0 8px 24px rgba(0, 0, 0, 0.5)"
      : "0 4px 12px rgba(0, 0, 0, 0.3)",
    zIndex: 30,
    transition: isDragging ? "none" : "box-shadow 0.2s ease",
    touchAction: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const containerStyle = isFullScreen ? fullScreenStyle : floatingStyle;

  const videoStyle: React.CSSProperties = isFullScreen
    ? {
        width: "100%",
        height: "auto",
        maxHeight: "100%",
        objectFit: "contain",
        backgroundColor: "#000",
        pointerEvents: "none",
      }
    : {
        // Collapsed: fill container and crop to cover
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        backgroundColor: "#000",
        pointerEvents: "none",
      };

  // Render LiveKit player if using LiveKit
  // LiveKit has its own controls (member name, mute button) via BroadcastC2CRTCViewer__info
  if (currentC2C.isLivekit) {
    return (
      <div
        ref={containerRef}
        style={containerStyle}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}>
        <TailwindBox
          width='w-full'
          height={isFullScreen ? "h-auto" : "h-full"}
          backgroundColor='bg-black'
          className={["mobile-c2c-livekit"]}
          style={
            isFullScreen
              ? {
                  maxHeight: "100%",
                }
              : {
                  // Collapsed: fill container completely
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }
          }>
          <LiveKitCam2Cam
            userName={profile.username}
            userId={profileStore!.broadcastId!}
            memberName={currentC2C.memberName}
            room={`${currentC2C.memberName}+${profile.username}`.toLowerCase()}
            onDisconnect={() => {}}
            onSuccess={() => {}}
            onFail={() => {}}
          />
        </TailwindBox>

        {/* Navigation arrows (only if multiple viewers) */}
        {c2cList.length > 1 && (
          <>
            {/* Counter overlay */}
            <TailwindFlex
              position='absolute'
              inset={["top-1", "left-1"]}
              padding={["px-2", "py-1"]}
              backgroundColor='bg-black'
              opacity='opacity-50'
              borderRadius='rounded-full'>
              <TailwindText
                textColor='text-white'
                fontSize={isFullScreen ? "text-lg" : "text-xs"}>
                {currentIndex + 1}/{c2cList.length}
              </TailwindText>
            </TailwindFlex>
            <TailwindBox
              as='button'
              position='absolute'
              inset={
                isFullScreen ? ["left-4", "top-1/2"] : ["left-1", "top-1/2"]
              }
              style={{ transform: "translateY(-50%)", pointerEvents: "auto" }}
              onClick={handlePrevViewer}
              backgroundColor='bg-black'
              opacity='opacity-50'
              borderRadius='rounded-full'
              padding={isFullScreen ? ["p-3"] : ["p-1"]}>
              <TailwindIcon
                name='chevron_left'
                textColor='text-white'
                fontSize={isFullScreen ? "text-3xl" : "text-sm"}
              />
            </TailwindBox>
            <TailwindBox
              as='button'
              position='absolute'
              inset={
                isFullScreen ? ["right-4", "top-1/2"] : ["right-1", "top-1/2"]
              }
              style={{ transform: "translateY(-50%)", pointerEvents: "auto" }}
              onClick={handleNextViewer}
              backgroundColor='bg-black'
              opacity='opacity-50'
              borderRadius='rounded-full'
              padding={isFullScreen ? ["p-3"] : ["p-1"]}>
              <TailwindIcon
                name='chevron_right'
                textColor='text-white'
                fontSize={isFullScreen ? "text-3xl" : "text-sm"}
              />
            </TailwindBox>
          </>
        )}

        {/* Expand/collapse button */}
        <TailwindBox
          as='button'
          position='absolute'
          inset={isFullScreen ? ["top-4", "right-4"] : ["top-1", "right-1"]}
          onClick={toggleFullScreen}
          backgroundColor='bg-black'
          opacity='opacity-50'
          borderRadius='rounded-full'
          padding={isFullScreen ? ["p-3"] : ["p-1"]}
          style={{ pointerEvents: "auto" }}>
          <TailwindIcon
            name={isFullScreen ? "close_fullscreen" : "open_in_full"}
            textColor='text-white'
            fontSize={isFullScreen ? "text-2xl" : "text-sm"}
          />
        </TailwindBox>
      </div>
    );
  }

  // Render WebRTC player
  return (
    <div
      ref={containerRef}
      style={containerStyle}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}>
      <video
        ref={videoRef}
        style={videoStyle}
        autoPlay
        playsInline
        muted={muted}
        onLoadedData={() => setWebRTCStatus(WebRTCStatus.Playing)}
      />

      {/* Overlay controls at bottom */}
      <TailwindFlex
        position='absolute'
        inset={["bottom-0", "left-0", "right-0"]}
        padding={isFullScreen ? ["p-4"] : ["p-1.5"]}
        justifyContent='justify-between'
        alignItems='items-center'
        style={{
          background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
          pointerEvents: "auto",
        }}>
        <TailwindFlex
          alignItems='items-center'
          gap='gap-2'
          flexGrow='flex-grow'
          style={{ minWidth: 0 }}>
          <TailwindBox
            as='button'
            onClick={toggleMute}
            style={{ pointerEvents: "auto" }}>
            <TailwindIcon
              name={muted ? "volume_off" : "volume_up"}
              textColor='text-white'
              fontSize={isFullScreen ? "text-2xl" : "text-sm"}
            />
          </TailwindBox>
          <TailwindText
            textColor='text-white'
            fontSize={isFullScreen ? "text-lg" : "text-xs"}
            overflow='overflow-hidden'
            style={{ textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {currentC2C.memberName}
          </TailwindText>
        </TailwindFlex>
        {c2cList.length > 1 && (
          <TailwindText
            textColor='text-white'
            fontSize={isFullScreen ? "text-lg" : "text-xs"}
            style={{ opacity: 0.7 }}>
            {currentIndex + 1}/{c2cList.length}
          </TailwindText>
        )}
      </TailwindFlex>

      {/* Navigation arrows for multiple viewers */}
      {c2cList.length > 1 && (
        <>
          <TailwindBox
            as='button'
            position='absolute'
            inset={isFullScreen ? ["left-4", "top-1/2"] : ["left-1", "top-1/2"]}
            style={{ transform: "translateY(-50%)", pointerEvents: "auto" }}
            onClick={handlePrevViewer}
            backgroundColor='bg-black'
            opacity='opacity-50'
            borderRadius='rounded-full'
            padding={isFullScreen ? ["p-3"] : ["p-1"]}>
            <TailwindIcon
              name='chevron_left'
              textColor='text-white'
              fontSize={isFullScreen ? "text-3xl" : "text-sm"}
            />
          </TailwindBox>
          <TailwindBox
            as='button'
            position='absolute'
            inset={
              isFullScreen ? ["right-4", "top-1/2"] : ["right-1", "top-1/2"]
            }
            style={{ transform: "translateY(-50%)", pointerEvents: "auto" }}
            onClick={handleNextViewer}
            backgroundColor='bg-black'
            opacity='opacity-50'
            borderRadius='rounded-full'
            padding={isFullScreen ? ["p-3"] : ["p-1"]}>
            <TailwindIcon
              name='chevron_right'
              textColor='text-white'
              fontSize={isFullScreen ? "text-3xl" : "text-sm"}
            />
          </TailwindBox>
        </>
      )}

      {/* Expand/collapse button */}
      <TailwindBox
        as='button'
        position='absolute'
        inset={isFullScreen ? ["top-4", "right-4"] : ["top-1", "right-1"]}
        onClick={toggleFullScreen}
        backgroundColor='bg-black'
        opacity='opacity-50'
        borderRadius='rounded-full'
        padding={isFullScreen ? ["p-3"] : ["p-1"]}
        style={{ pointerEvents: "auto" }}>
        <TailwindIcon
          name={isFullScreen ? "close_fullscreen" : "open_in_full"}
          textColor='text-white'
          fontSize={isFullScreen ? "text-2xl" : "text-sm"}
        />
      </TailwindBox>
    </div>
  );
};

export default inject(
  "broadcastStore",
  "broadcastStreamStore",
  "profileStore"
)(observer(MobileBroadcastC2C));
